import React, { memo } from 'react';
import AgentAvatar from '../components/AgentAvatar';
import ChatMarkdown from './ChatMarkdown';
import StreamText from './StreamText';
import './chat-messages.css';

const ThinkingIndicator = ({ tip = '正在思考' }) => (
  <div className="chat-thinking">
    <span className="chat-thinking__label">{tip}</span>
    <span className="chat-thinking__dots" aria-hidden="true">
      <span />
      <span />
      <span />
    </span>
  </div>
);

const UserMessage = memo(({ msg }) => (
  <div className={`chat-bubble-user${msg.images?.length ? ' chat-bubble-user--images' : ''}`}>
    {msg.content && <div>{msg.content}</div>}
    {msg.images?.length > 0 && (
      <div className="chat-user-images">
        {msg.images.map((image, imgIdx) => (
          <div key={imgIdx} className="chat-user-image-card">
            <img src={image.url} alt={image.name} />
            <span>{image.name}</span>
          </div>
        ))}
      </div>
    )}
  </div>
));

UserMessage.displayName = 'UserMessage';

const AssistantMessage = memo(({ msg, loadingTip }) => {
  if (msg.isThinking && !msg.content) {
    return <ThinkingIndicator tip={loadingTip} />;
  }

  const content = (msg.content || '').replace(/^\n+/, '');

  if (msg.isRealtime && content) {
    return (
      <div className="chat-markdown-wrap chat-markdown-wrap--streaming">
        <StreamText content={content} />
      </div>
    );
  }

  return (
    <div className="chat-markdown-wrap">
      <ChatMarkdown
        content={content}
        className={msg.isError ? 'chat-markdown--error' : ''}
      />
    </div>
  );
});

AssistantMessage.displayName = 'AssistantMessage';

const ChatMessageRow = memo(({ msg, loadingTip, currentAgent }) => {
  const isUser = msg.role === 'user';

  return (
    <div className={`chat-row chat-row--${isUser ? 'user' : 'assistant'}`}>
      {!isUser && (
        <div className="chat-row__avatar">
          <AgentAvatar agent={currentAgent} size={28} />
        </div>
      )}
      <div className="chat-row__body">
        {isUser ? (
          <UserMessage msg={msg} />
        ) : (
          <AssistantMessage
            msg={msg}
            loadingTip={loadingTip}
            currentAgent={currentAgent}
          />
        )}
      </div>
    </div>
  );
});

ChatMessageRow.displayName = 'ChatMessageRow';

const ChatMessageList = ({
  messages = [],
  showLoading = false,
  loadingTip = '正在思考',
  chatListRef,
  currentAgent = null,
  customStyles = {},
  renderMessage,
  scrollable = true,
}) => (
  <div
    ref={chatListRef}
    className="chat-thread"
    style={{
      ...(scrollable ? { flex: 1, minHeight: 300, overflowY: 'auto' } : {}),
      ...customStyles,
    }}
  >
    {messages.map((msg, idx) => (
      renderMessage ? (
        renderMessage(msg, idx)
      ) : (
        <ChatMessageRow
          key={msg.id || `msg-${idx}`}
          msg={msg}
          loadingTip={loadingTip}
          currentAgent={currentAgent}
        />
      )
    ))}
    {showLoading && (
      <div className="chat-row chat-row--assistant">
        <div className="chat-row__avatar">
          <AgentAvatar agent={currentAgent} size={28} />
        </div>
        <div className="chat-row__body">
          <ThinkingIndicator tip={loadingTip} />
        </div>
      </div>
    )}
  </div>
);

export default memo(ChatMessageList);
